import { TimesheetStatus, type Timesheet, type TimesheetRow, type WeekDay } from '../types';
import { getWeekStatus } from './formatters';
import { getTimesheetWorkdayWeekDays } from './timesheetWorkdayColumns';

const ALL_DAYS: WeekDay[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];

export function isSubmittedTimesheetStatus(status: Timesheet['status']): boolean {
  return (
    status === TimesheetStatus.PENDING_ACCOUNTANT ||
    status === TimesheetStatus.PENDING_OP_MANAGER ||
    status === TimesheetStatus.PENDING_DEAL_OWNERS ||
    status === TimesheetStatus.CLOSED
  );
}

/** API may return numeric or string status codes. */
export function isPendingAccountantTimesheetStatus(status: Timesheet['status'] | undefined): boolean {
  if (status === undefined || status === null) return false;
  return String(status) === String(TimesheetStatus.PENDING_ACCOUNTANT);
}

/** Issued/Rejected and calendar week is current or past (not “coming up”). */
export function isCopyTargetEligible(ts: Timesheet, currentTimesheetId: number): boolean {
  if (ts.id === currentTimesheetId) return false;
  if (ts.status !== TimesheetStatus.ISSUED && ts.status !== TimesheetStatus.REJECTED) return false;
  const cal = getWeekStatus(ts.weekStart);
  return cal === 'New' || cal === 'Overdue';
}

/** True only when the timesheet has configured holidays (not partial weeks with fewer workdays). */
export function targetWeekHolidayCopyWarning(ts: Timesheet): boolean {
  return (ts.holidays?.length ?? 0) > 0;
}

export function selectedRowsSkipSourceHolidayColumns(
  selectedRows: TimesheetRow[],
  sourceWeekStart: string,
  sourceWeekDates: string[]
): boolean {
  const sourceKeys = new Set(getTimesheetWorkdayWeekDays(sourceWeekStart, sourceWeekDates));
  for (const row of selectedRows) {
    for (const d of ALL_DAYS) {
      if (!sourceKeys.has(d) && Number(row[d]) > 0) return true;
    }
  }
  return false;
}

export type CellPatch = { rowId: number; payload: Partial<Record<WeekDay, number>> };

/**
 * Map source rows to destination rows by deal + row label.
 * dealId alone is not unique (many special rows use dealId 0); name disambiguates.
 */
function findDestinationRow(destRows: TimesheetRow[], srcRow: TimesheetRow): TimesheetRow | undefined {
  return destRows.find((r) => r.dealId === srcRow.dealId && r.name === srcRow.name);
}

export function buildCopyCellPatches(
  selectedSourceRows: TimesheetRow[],
  sourceWeekStart: string,
  sourceWeekDates: string[],
  targetTimesheets: Timesheet[]
): CellPatch[] {
  const sourceKeys = getTimesheetWorkdayWeekDays(sourceWeekStart, sourceWeekDates);
  const sourceKeySet = new Set(sourceKeys);
  const patches: CellPatch[] = [];

  for (const targetTs of targetTimesheets) {
    const destKeys = new Set(getTimesheetWorkdayWeekDays(targetTs.weekStart, targetTs.weekDates ?? []));
    const copyKeys = ALL_DAYS.filter((d) => sourceKeySet.has(d) && destKeys.has(d));
    if (!copyKeys.length) continue;

    const destRows = targetTs.timesheetRows ?? [];
    for (const srcRow of selectedSourceRows) {
      const destRow = findDestinationRow(destRows, srcRow);
      if (!destRow) continue;
      const payload: Partial<Record<WeekDay, number>> = {};
      for (const k of copyKeys) {
        payload[k] = Number(srcRow[k]);
      }
      if (Object.keys(payload).length > 0) {
        patches.push({ rowId: destRow.id, payload });
      }
    }
  }

  return patches;
}
